import React, { useState, useMemo } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  Check,
  X,
  Edit2,
  Settings,
  TrendingUp
} from 'lucide-react';
import { PRODUCT_STATUS, CISA_FUNCTIONS, CISA_PILLARS } from '../../config/cisaMaturityModel';

/**
 * TechStackTable - Sortable table displaying technology stack with coverage and config status
 *
 * Props:
 * - productAdoptions: { [functionCode]: [{ productName, status, ... }] }
 * - configCompletions: { [functionCode]: { [productName]: { [stepId]: boolean } } }
 * - derivedScores: { [functionCode]: DerivedScoreObject }
 * - pillarFilter: string | null (filter by pillar code)
 * - showConfigStatus: boolean (show config status column)
 * - onUpdateProduct: (functionCode, productName, updates) => void
 * - onRemoveProduct: (functionCode, productName) => void
 * - onAddProduct: (functionCode, productName, status) => void
 * - onViewConfig: (functionCode, productName) => void
 */
const TechStackTable = ({
  productAdoptions = {},
  configCompletions = {},
  derivedScores = {},
  pillarFilter = null,
  showConfigStatus = false,
  onUpdateProduct,
  onRemoveProduct,
  onAddProduct,
  onViewConfig
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'vendor', direction: 'asc' });
  const [editingRow, setEditingRow] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newProduct, setNewProduct] = useState({ vendor: '', product: '', functionCode: '', status: 'EVALUATING', coverage: 50 });

  // Calculate config status from completions
  const calculateConfigStatus = (functionCode, productName) => {
    const fnConfigs = configCompletions[functionCode] || {};
    const productConfigs = fnConfigs[productName] || {};
    const steps = Object.values(productConfigs);

    if (steps.length === 0) {
      return { status: 'NOT_CONFIGURED', completedSteps: 0, totalSteps: 0, percentage: 0 };
    }

    const completedSteps = steps.filter(s => s === true).length;
    const totalSteps = steps.length;
    const percentage = Math.round((completedSteps / totalSteps) * 100);

    let status;
    if (percentage === 100) {
      status = 'FULLY_CONFIGURED';
    } else if (percentage > 0) {
      status = 'PARTIALLY_CONFIGURED';
    } else {
      status = 'NOT_CONFIGURED';
    }

    return { status, completedSteps, totalSteps, percentage };
  };

  // Build flat list of products with function info
  const products = useMemo(() => {
    const items = [];

    // Get functions to include based on filter
    const pillarCodes = pillarFilter ? [pillarFilter] : Object.keys(CISA_FUNCTIONS);

    for (const pillarCode of pillarCodes) {
      const pillar = CISA_PILLARS.find(p => p.code === pillarCode);
      const functions = CISA_FUNCTIONS[pillarCode] || [];

      for (const fn of functions) {
        const fnProducts = productAdoptions[fn.code] || [];
        const fnDerivedScore = derivedScores[fn.code];

        for (const product of fnProducts) {
          // Extract vendor from product name (heuristic: first word before space or parenthesis)
          const vendorMatch = product.productName.match(/^([A-Za-z0-9]+)/);
          const vendor = vendorMatch ? vendorMatch[1] : 'Unknown';

          // Calculate actual config status
          const configInfo = showConfigStatus
            ? calculateConfigStatus(fn.code, product.productName)
            : { status: 'UNKNOWN', percentage: 0 };

          items.push({
            id: `${fn.code}-${product.productName}`,
            functionCode: fn.code,
            functionName: fn.name,
            pillarCode,
            pillarName: pillar?.name || pillarCode,
            pillarColor: pillar?.color || '#6b7280',
            vendor,
            productName: product.productName,
            status: product.status || 'EVALUATING',
            coverage: product.coverage ?? 80, // Default coverage
            configStatus: configInfo.status,
            configPercentage: configInfo.percentage,
            configCompletedSteps: configInfo.completedSteps,
            configTotalSteps: configInfo.totalSteps,
            maturityContribution: fnDerivedScore?.capabilityCoverage || 0,
            adoptedAt: product.adoptedAt
          });
        }
      }
    }

    return items;
  }, [productAdoptions, configCompletions, derivedScores, pillarFilter, showConfigStatus]);

  // Sort products
  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle string comparison
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [products, sortConfig]);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={14} />;
    return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Get status info
  const getStatusInfo = (status) => {
    return PRODUCT_STATUS.find(s => s.key === status) || PRODUCT_STATUS[2]; // Default to EVALUATING
  };

  // Get config status styling
  const getConfigStatusStyle = (status, percentage) => {
    switch (status) {
      case 'FULLY_CONFIGURED':
        return { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', label: 'Configured' };
      case 'PARTIALLY_CONFIGURED':
        return { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', label: `${percentage}%` };
      case 'NOT_CONFIGURED':
        return { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280', label: 'Not Configured' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280', label: 'Unknown' };
    }
  };

  // Get available functions for add row
  const availableFunctions = useMemo(() => {
    const pillarCodes = pillarFilter ? [pillarFilter] : Object.keys(CISA_FUNCTIONS);
    const fns = [];
    for (const pillarCode of pillarCodes) {
      const functions = CISA_FUNCTIONS[pillarCode] || [];
      for (const fn of functions) {
        fns.push({ code: fn.code, name: fn.name, pillarCode });
      }
    }
    return fns;
  }, [pillarFilter]);

  // Handle add product
  const handleAddProduct = () => {
    if (newProduct.product && newProduct.functionCode) {
      onAddProduct?.(newProduct.functionCode, newProduct.product, newProduct.status);
      setNewProduct({ vendor: '', product: '', functionCode: '', status: 'EVALUATING', coverage: 50 });
      setShowAddRow(false);
    }
  };

  return (
    <div className="cisa-tech-stack-table-wrap">
      <table className="cisa-tech-stack-table">
        <thead>
          <tr>
            <th>
              <button type="button" onClick={() => handleSort('vendor')} className="cisa-table-sort-btn">
                Vendor {getSortIcon('vendor')}
              </button>
            </th>
            <th>
              <button type="button" onClick={() => handleSort('productName')} className="cisa-table-sort-btn">
                Product {getSortIcon('productName')}
              </button>
            </th>
            <th>
              <button type="button" onClick={() => handleSort('functionName')} className="cisa-table-sort-btn">
                CISA Function {getSortIcon('functionName')}
              </button>
            </th>
            <th>
              <button type="button" onClick={() => handleSort('coverage')} className="cisa-table-sort-btn">
                Coverage {getSortIcon('coverage')}
              </button>
            </th>
            {showConfigStatus && (
              <th>
                <button type="button" onClick={() => handleSort('configPercentage')} className="cisa-table-sort-btn">
                  Config Status {getSortIcon('configPercentage')}
                </button>
              </th>
            )}
            <th>
              <button type="button" onClick={() => handleSort('status')} className="cisa-table-sort-btn">
                Status {getSortIcon('status')}
              </button>
            </th>
            <th className="cisa-table-actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedProducts.map((product) => {
            const statusInfo = getStatusInfo(product.status);
            const configStyle = getConfigStatusStyle(product.configStatus, product.configPercentage);
            const isEditing = editingRow === product.id;

            return (
              <tr key={product.id} className="cisa-tech-stack-row">
                <td>
                  <span className="cisa-tech-stack-vendor">{product.vendor}</span>
                </td>
                <td>
                  <span className="cisa-tech-stack-product">{product.productName}</span>
                </td>
                <td>
                  <div className="cisa-tech-stack-function">
                    <span
                      className="cisa-tech-stack-pillar-dot"
                      style={{ background: product.pillarColor }}
                    />
                    <span>{product.functionName}</span>
                  </div>
                </td>
                <td>
                  <div className="cisa-tech-stack-coverage">
                    <div className="cisa-tech-stack-coverage-bar">
                      <div
                        className="cisa-tech-stack-coverage-fill"
                        style={{
                          width: `${product.coverage}%`,
                          background: product.coverage >= 80 ? '#10b981' : product.coverage >= 50 ? '#f59e0b' : '#ef4444'
                        }}
                      />
                    </div>
                    <span className="cisa-tech-stack-coverage-text">{product.coverage}%</span>
                  </div>
                </td>
                {showConfigStatus && (
                  <td>
                    <button
                      type="button"
                      className="cisa-tech-stack-config-btn"
                      style={{ background: configStyle.bg, color: configStyle.color }}
                      onClick={() => onViewConfig?.(product.functionCode, product.productName)}
                      title={`${product.configCompletedSteps}/${product.configTotalSteps} steps completed`}
                    >
                      <Settings size={12} />
                      <span>{configStyle.label}</span>
                    </button>
                  </td>
                )}
                <td>
                  <span
                    className="cisa-tech-stack-status-badge"
                    style={{ background: `${statusInfo.color}20`, color: statusInfo.color }}
                  >
                    <span className="cisa-tech-stack-status-icon">{statusInfo.icon}</span>
                    {statusInfo.label}
                  </span>
                </td>
                <td>
                  <div className="cisa-tech-stack-actions">
                    <button
                      type="button"
                      className="cisa-tech-stack-action-btn"
                      onClick={() => setEditingRow(isEditing ? null : product.id)}
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      type="button"
                      className="cisa-tech-stack-action-btn cisa-tech-stack-action-btn--danger"
                      onClick={() => onRemoveProduct?.(product.functionCode, product.productName)}
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}

          {/* Add row */}
          {showAddRow && (
            <tr className="cisa-tech-stack-row cisa-tech-stack-row--add">
              <td>
                <input
                  type="text"
                  value={newProduct.vendor}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, vendor: e.target.value }))}
                  placeholder="Vendor"
                  className="cisa-tech-stack-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={newProduct.product}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, product: e.target.value }))}
                  placeholder="Product name"
                  className="cisa-tech-stack-input"
                />
              </td>
              <td>
                <select
                  value={newProduct.functionCode}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, functionCode: e.target.value }))}
                  className="cisa-tech-stack-select"
                >
                  <option value="">Select function...</option>
                  {availableFunctions.map(fn => (
                    <option key={fn.code} value={fn.code}>{fn.name}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={newProduct.coverage}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, coverage: parseInt(e.target.value) || 0 }))}
                  className="cisa-tech-stack-input cisa-tech-stack-input--small"
                />
              </td>
              {showConfigStatus && (
                <td>
                  <span className="cisa-tech-stack-config-badge-static">
                    New
                  </span>
                </td>
              )}
              <td>
                <select
                  value={newProduct.status}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, status: e.target.value }))}
                  className="cisa-tech-stack-select"
                >
                  {PRODUCT_STATUS.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </td>
              <td>
                <div className="cisa-tech-stack-actions">
                  <button
                    type="button"
                    className="cisa-tech-stack-action-btn cisa-tech-stack-action-btn--success"
                    onClick={handleAddProduct}
                    disabled={!newProduct.product || !newProduct.functionCode}
                    title="Add"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    type="button"
                    className="cisa-tech-stack-action-btn"
                    onClick={() => setShowAddRow(false)}
                    title="Cancel"
                  >
                    <X size={14} />
                  </button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Empty state */}
      {sortedProducts.length === 0 && !showAddRow && (
        <div className="cisa-tech-stack-empty">
          <p>No products tracked yet.</p>
          <button
            type="button"
            className="cisa-tech-stack-add-btn"
            onClick={() => setShowAddRow(true)}
          >
            <Plus size={14} />
            Add Product
          </button>
        </div>
      )}

      {/* Add button when table has rows */}
      {(sortedProducts.length > 0 || showAddRow) && !showAddRow && (
        <button
          type="button"
          className="cisa-tech-stack-add-row-btn"
          onClick={() => setShowAddRow(true)}
        >
          <Plus size={14} />
          Add Product
        </button>
      )}
    </div>
  );
};

export default TechStackTable;
